import type { FC } from 'react';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath } from 'react-router-dom';
import DownloadRounded from '@mui/icons-material/DownloadRounded';
import { Button } from '@mui/material';

import { apiPaths } from 'src/services/api/apiPaths';
import HttpServiceInstance from 'src/services/api/http';
import { useSection } from 'src/components/app/context/SectionContext';
import { useUserContext } from 'src/components/app/context/UserContext';
import SuccessSnackbar from 'src/components/common/Snackbar/SuccessSnackbar';
import { useDashboardCommentContext } from 'src/components/dashboard/DashboardContext/CommentsContext';
import { useDownloadManager } from 'src/components/downloadManager/DownloadManagerContext';
import useFollowUpFilters from 'src/components/followUp/hooks/useFollowUpFilters';

export interface CommentsListDownloadProps {
  topics: Array<string>;
  useTranslatedText: boolean;
}

const HRQSCommentsListDownload: FC<CommentsListDownloadProps> = ({
  topics,
  useTranslatedText,
}) => {
  const { t } = useTranslation('dashboard');
  const { textFilter, widgetId } = useDashboardCommentContext();
  const { moduleId } = useSection();
  const { poll } = useDownloadManager();
  const [downloadStarted, setDownloadStarted] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const { currentSurvey } = useUserContext();

  const { dateRange, followUpFilters, demographicsFilters, orgUnit } =
    useFollowUpFilters();

  const filtersToUse = useMemo(
    () => ({
      orgUnit,
      ...dateRange,
      ...followUpFilters,
      ...demographicsFilters,
      topicIds: topics.toString(),
      textFilter,
      ...(useTranslatedText && { use_translated_text: true }),
    }),
    [
      orgUnit,
      followUpFilters,
      demographicsFilters,
      dateRange,
      topics,
      textFilter,
      useTranslatedText,
    ],
  );

  useEffect(() => {
    if (isDisabled) {
      const timer = setTimeout(() => {
        setIsDisabled(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [isDisabled]);

  const endpoint = useMemo(
    () =>
      generatePath(apiPaths.WIDGET_DATA.OPEN_COMMENTS.EXPORT, {
        surveyId: currentSurvey?.id,
        widgetId: widgetId ?? 0,
      }),
    [apiPaths, currentSurvey?.id],
  );

  const handleDownloadClick = () => {
    if (!moduleId || !topics.length) return;

    setIsDisabled(true);
    HttpServiceInstance.get(endpoint, filtersToUse).then(() => {
      poll();
      setDownloadStarted(true);
    });
  };

  return (
    <>
      <Button
        onClick={handleDownloadClick}
        startIcon={<DownloadRounded />}
        disabled={isDisabled}
      >
        {t(`OpenCommentsList.DownloadComments`)}
      </Button>

      <SuccessSnackbar
        open={downloadStarted}
        onClose={() => setDownloadStarted(false)}
        title={t('downloadManager:DownloadManager')}
        message={t('OpenCommentsList.DownloadMessage')}
      />
    </>
  );
};

export default HRQSCommentsListDownload;
